import React, { forwardRef } from 'react';
import { format } from 'date-fns';

import type { OrderType, OrderInstruction } from '@/types';
import { renderRichText, richTextClass } from '@univdiam/ui/lib/renderRichText';
import { cn } from '@univdiam/ui/lib/utils';

type PrintTemplateProps = {
    orderDetailData: OrderType;
    OrderDetail: Record<string, string | number | boolean>;
    orderInstruction: OrderInstruction[];
};

const PrintTemplate = forwardRef<HTMLDivElement, PrintTemplateProps>(
    ({ orderDetailData, OrderDetail: orderDetailsConfig, orderInstruction }, ref) => {
        return (
            <div ref={ref} className="p-10 bg-white text-black w-full min-h-screen hidden-print-block print:block">
                <style type="text/css" media="print">
                    {`
            @page { size: auto; margin: 20mm; }
            body { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
            .hidden-print-block { display: none; }
            @media print { .hidden-print-block { display: block; } }
          `}
                </style>

                {/* Header: Job Title & Basic Info */}
                <div className="mb-8 border-b border-gray-200 pb-4">
                    <h1 className="text-3xl font-bold text-gray-900 mb-2">{orderDetailData?.job_title}</h1>
                    <p className="text-sm text-gray-500">Order Ref: {orderDetailData?.order_id}</p>
                    <p className="text-sm text-gray-500">
                        Created: {orderDetailData?.created_at ? format(new Date(orderDetailData.created_at), 'MMMM dd, yyyy') : '-'}
                    </p>
                </div>

                {/* Order Details Section */}
                <div className="mb-8">
                    <h2 className="text-xl font-semibold border-b border-gray-200 pb-2 mb-4">Order Details</h2>
                    <div className="grid grid-cols-2 gap-4 text-sm">
                        <div className="flex gap-2"><span className="font-semibold w-1/2">Order ID:</span> <span>{orderDetailsConfig.orderID}</span></div>
                        <div className="flex gap-2"><span className="font-semibold w-1/2">Date of Creation:</span> <span>{orderDetailsConfig.orderDate ? format(new Date(orderDetailsConfig.orderDate as string), 'MMMM dd, yyyy') : '-'}</span></div>
                        <div className="flex gap-2"><span className="font-semibold w-1/2">Resin Sample Model:</span> <span>{orderDetailsConfig.ResinSampleModel ? 'Provide' : 'None'}</span></div>
                        <div className="flex gap-2"><span className="font-semibold w-1/2">Type of Jewelry:</span> <span>{orderDetailsConfig.TypeofJewelry}</span></div>
                        <div className="flex gap-2"><span className="font-semibold w-1/2">Size:</span> <span>{orderDetailsConfig.Size || '-'}</span></div>
                        <div className="flex gap-2"><span className="font-semibold w-1/2">Metal Type:</span> <span>{orderDetailsConfig.MetalType || '-'}</span></div>
                        <div className="flex gap-2"><span className="font-semibold w-1/2">Quantity:</span> <span>{orderDetailsConfig.Quantity || '-'}</span></div>
                    </div>
                </div>

                {/* Special Instructions Section */}
                <div className="mb-8 bg-gray-50 p-4 border border-gray-200 rounded-md">
                    <h2 className="text-lg font-semibold mb-3 text-gray-800">Special Instructions</h2>
                    {orderDetailData?.special_instructions ? (
                        <div
                            className={cn("text-sm text-gray-700 leading-relaxed", richTextClass)}
                            dangerouslySetInnerHTML={{ __html: renderRichText(orderDetailData.special_instructions) }}
                        />
                    ) : (
                        <p className="text-sm text-gray-500 italic">No special instructions</p>
                    )}
                </div>

                {/* Attachments Section */}
                <div className="mb-8">
                    <h2 className="text-xl font-semibold border-b border-gray-200 pb-2 mb-4">Attachments</h2>
                    <div className="grid grid-cols-3 gap-4">
                        {orderDetailData?.media_attachment && orderDetailData.media_attachment.length > 0 ? (
                            orderDetailData.media_attachment.map((attachmentUrl: string, idx: number) => {
                                const extension = attachmentUrl.split('.').pop()?.toLowerCase();
                                const isImage = ['jpg', 'jpeg', 'png', 'gif', 'webp', 'bmp'].includes(extension || '');
                                return (
                                    <div key={idx} className="border border-gray-200 p-2 rounded flex flex-col items-center justify-center">
                                        {isImage ? (
                                            <img src={attachmentUrl} alt={`Attachment ${idx + 1}`} className="max-h-32 object-contain mb-2" />
                                        ) : (
                                            <div className="h-16 flex items-center justify-center text-sm font-medium text-gray-500">
                                                {extension?.toUpperCase() || 'FILE'}
                                            </div>
                                        )}
                                        <a href={attachmentUrl} download className="text-xs text-blue-600 truncate w-full text-center" rel="noreferrer">
                                            Download File
                                        </a>
                                    </div>
                                );
                            })
                        ) : (
                            <p className="text-sm text-gray-500 italic">No attachments</p>
                        )}
                    </div>
                </div>

                {/* Additional Instructions Section */}
                <div className="mb-8">
                    <h2 className="text-xl font-semibold border-b border-gray-200 pb-2 mb-4">Additional Instructions</h2>
                    {orderInstruction && orderInstruction.length > 0 ? (
                        <div className="space-y-4">
                            {orderInstruction.map((inst, index) => (
                                <div key={index} className="flex flex-col gap-1 border-b border-gray-100 pb-2">
                                    <span className="text-sm font-bold text-gray-800">{inst.label}</span>
                                    <span className="text-sm text-gray-600">{inst.value}</span>
                                </div>
                            ))}
                        </div>
                    ) : (
                        <p className="text-sm text-gray-500 italic">No additional instructions</p>
                    )}
                </div>
            </div>
        );
    }
);

PrintTemplate.displayName = 'PrintTemplate';

export default PrintTemplate;
